import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/axiosConfig';
import ClienteMultiSelect from '../components/ClienteMultiSelect';
import AirportAutocomplete from '../components/AirportAutocomplete';
import AirlineAutocomplete from '../components/AirlineAutocomplete';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip'; 
import { toast } from 'sonner';
import { Plus, Trash2, Info } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';

const CATEGORIAS_DISPONIVEIS = ['AEREO', 'HOSPEDAGEM', 'AUTOMOVEL', 'TRANSFER', 'PASSEIO', 'SEGURO', 'OUTROS'];

export default function NovaVenda() {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contas, setContas] = useState([]);
  const [defaultContaId, setDefaultContaId] = useState('');

  const [formData, setFormData] = useState({
    clientes_ids: [],
    categorias_selecionadas: [], 
    vendedor_id: '',
    fornecedor_id: '',
    localizador: '',
    observacao: '',
    liquidar_recebimentos: false,
    liquidar_custos: false,
    
    aereo_custo: '',
    aereo_venda: '',

    trechos: [],
    hospedagens: [],
    automoveis: [],
    transfers: [],
    passeios: [],
    seguros: [],
    outros: [],

    bagagens: [],        
    reservas: [],
    pets: [],        
    descontos: [],       
    
    valor_total: 0,
    custo: 0,
    lucro: 0,
    
    pagamentos: [{ id: 0, forma_pagamento: 'PIX', valor: '', num_parcelas: 1, data_vencimento: '', conta_id: '' }], 
    pagamentos_custo: [{ id: 0, forma_pagamento: 'BOLETO', valor: '', num_parcelas: 1, data_vencimento: '', conta_id: '' }]
  });
  
  const [paymentCounter, setPaymentCounter] = useState(1); 
  const [costCounter, setCostCounter] = useState(1);
  const [lineCounter, setLineCounter] = useState(1);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientesRes, fornecedoresRes, vendedoresRes, contasRes] = await Promise.all([
          axiosInstance.get('/clientes'),
          axiosInstance.get('/fornecedores'),
          axiosInstance.get('/vendedores'),
          axiosInstance.get('/contas'), 
        ]);
        setClientes(clientesRes.data);
        setFornecedores(fornecedoresRes.data);
        setVendedores(vendedoresRes.data);
        
        const contasData = contasRes.data || [];
        setContas(contasData);
        let dContaId = '';
        if (contasData.length > 0) {
          dContaId = contasData[0].id; 
          setDefaultContaId(dContaId);
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        setFormData((prev) => ({ 
          ...prev, 
          vendedor_id: (user.vendedor_id && !user.is_superuser) ? user.vendedor_id : '',
          pagamentos: [{ ...prev.pagamentos[0], conta_id: dContaId }],
          pagamentos_custo: [{ ...prev.pagamentos_custo[0], conta_id: dContaId }]
        }));
      } catch (error) {
        toast.error('Erro ao carregar dados');
      }
    };
    loadData();
  }, []);

  const toggleCategoria = (cat) => {
    setFormData(prev => {
        const ativando = !prev.categorias_selecionadas.includes(cat);
        const novasCategorias = ativando 
            ? [...prev.categorias_selecionadas, cat] 
            : prev.categorias_selecionadas.filter(c => c !== cat);
        
        let novasListas = {};
        if (ativando) {
            if (cat === 'AEREO' && prev.trechos.length === 0) novasListas.trechos = [{ tipo: 'IDA', companhia_id: '', companhia_nome: '', origem: '', destino: '', data_partida: '', data_chegada: '', localizador: '', fornecedor_nome: '' }];
            if (cat === 'HOSPEDAGEM' && prev.hospedagens.length === 0) novasListas.hospedagens = [{ hotel: '', checkin: '', checkout: '', quarto: '', valor_custo: '', valor_venda: '', localizador: '', fornecedor_nome: '' }];
            if (cat === 'AUTOMOVEL' && prev.automoveis.length === 0) novasListas.automoveis = [{ locadora: '', modelo: '', retirada: '', devolucao: '', valor_custo: '', valor_venda: '', localizador: '', fornecedor_nome: '' }];
            if (cat === 'TRANSFER' && prev.transfers.length === 0) novasListas.transfers = [{ trajeto: '', data: '', valor_custo: '', valor_venda: '', localizador: '', fornecedor_nome: '' }];
            if (cat === 'PASSEIO' && prev.passeios.length === 0) novasListas.passeios = [{ descricao: '', data: '', valor_custo: '', valor_venda: '', localizador: '', fornecedor_nome: '' }];
            if (cat === 'SEGURO' && prev.seguros.length === 0) novasListas.seguros = [{ seguradora: '', plano: '', data_inicio: '', data_fim: '', valor_custo: '', valor_venda: '', localizador: '', fornecedor_nome: '' }];
            if (cat === 'OUTROS' && prev.outros.length === 0) novasListas.outros = [{ descricao: '', valor_custo: '', valor_venda: '' }];
        }
        return { ...prev, categorias_selecionadas: novasCategorias, ...novasListas };
    });
  };

  const addTrechoInteligente = () => {
    const trechos = formData.trechos;
    const last = trechos.length > 0 ? trechos[trechos.length - 1] : null;
    const novoTrecho = { tipo: last?.tipo === 'IDA' ? 'VOLTA' : 'IDA', companhia_id: last?.companhia_id || '', companhia_nome: last?.companhia_nome || '', origem: last?.destino || '', destino: last?.origem || '', data_partida: '', data_chegada: '', localizador: last?.localizador || '', fornecedor_nome: last?.fornecedor_nome || '' };
    setFormData(prev => ({ ...prev, trechos: [...prev.trechos, novoTrecho] }));
  };

  const addItem = (lista) => {
    const defaultTemplates = {
        hospedagens: { hotel: '', checkin: '', checkout: '', quarto: '', valor_custo: '', valor_venda: '', localizador: '', fornecedor_nome: '' },
        automoveis: { locadora: '', modelo: '', retirada: '', devolucao: '', valor_custo: '', valor_venda: '', localizador: '', fornecedor_nome: '' },
        transfers: { trajeto: '', data: '', valor_custo: '', valor_venda: '', localizador: '', fornecedor_nome: '' },
        passeios: { descricao: '', data: '', valor_custo: '', valor_venda: '', localizador: '', fornecedor_nome: '' },
        seguros: { seguradora: '', plano: '', data_inicio: '', data_fim: '', valor_custo: '', valor_venda: '', localizador: '', fornecedor_nome: '' },
        outros: { descricao: '', valor_custo: '', valor_venda: '' }
    };
    setFormData(prev => ({ ...prev, [lista]: [...prev[lista], defaultTemplates[lista]] }));
  };

  const updateItem = (lista, index, field, value) => {
    const novaLista = [...formData[lista]];
    novaLista[index][field] = value;
    setFormData({ ...formData, [lista]: novaLista });
  };

  const removeItem = (lista, index) => setFormData(prev => ({ ...prev, [lista]: prev[lista].filter((_, i) => i !== index) }));

  const addPagamento = () => {
    setPaymentCounter(c => c + 1);
    const totalJaLancado = formData.pagamentos.reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
    const valorRestante = formData.valor_total - totalJaLancado;
    setFormData(prev => ({ ...prev, pagamentos: [...prev.pagamentos, { id: paymentCounter, forma_pagamento: 'PIX', valor: valorRestante > 0.01 ? valorRestante.toFixed(2) : '', num_parcelas: 1, data_vencimento: '', conta_id: defaultContaId }] }));
  };

  const addPagamentoCusto = () => {
    setCostCounter(c => c + 1);
    const totalJaLancado = formData.pagamentos_custo.reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
    const valorRestante = formData.custo - totalJaLancado;
    setFormData(prev => ({ ...prev, pagamentos_custo: [...prev.pagamentos_custo, { id: costCounter, forma_pagamento: 'BOLETO', valor: valorRestante > 0.01 ? valorRestante.toFixed(2) : '', num_parcelas: 1, data_vencimento: '', conta_id: defaultContaId }] }));
  };

  const updatePagamento = (id, field, value) => setFormData(prev => ({ ...prev, pagamentos: prev.pagamentos.map(p => p.id === id ? { ...p, [field]: value } : p) }));
  const removePagamento = (id) => { if (formData.pagamentos.length <= 1) return toast.info("Deve haver pelo menos um recebimento."); setFormData(prev => ({ ...prev, pagamentos: prev.pagamentos.filter(p => p.id !== id) })); };
  const updatePagamentoCusto = (id, field, value) => setFormData(prev => ({ ...prev, pagamentos_custo: prev.pagamentos_custo.map(p => p.id === id ? { ...p, [field]: value } : p) }));
  const removePagamentoCusto = (id) => setFormData(prev => ({ ...prev, pagamentos_custo: prev.pagamentos_custo.filter(p => p.id !== id) }));

  const addExtra = (lista) => {
    setLineCounter(c => c + 1);
    setFormData(prev => ({ ...prev, [lista]: [...prev[lista], { id: lineCounter, valor_custo: '', valor_venda: '' }] }));
  };
  const updateExtra = (lista, id, field, valor) => {
    if (lista === 'descontos') valor = (parseFloat(valor) || 0) > 0 ? -(parseFloat(valor) || 0) : (parseFloat(valor) || 0);
    setFormData(prev => ({ ...prev, [lista]: prev[lista].map(i => i.id === id ? { ...i, [field]: valor } : i) }));
  };
  const removeExtra = (lista, id) => setFormData(prev => ({ ...prev, [lista]: prev[lista].filter(i => i.id !== id) }));

  // CÁLCULO GERAL
  useEffect(() => {
    const somarProp = (arr, prop) => arr.reduce((acc, item) => acc + (parseFloat(item[prop]) || 0), 0);
    const custoAereo = parseFloat(formData.aereo_custo) || 0;
    const vendaAereo = parseFloat(formData.aereo_venda) || 0;

    const custoProdutos = custoAereo + somarProp(formData.hospedagens, 'valor_custo') + somarProp(formData.automoveis, 'valor_custo') + somarProp(formData.transfers, 'valor_custo') + somarProp(formData.passeios, 'valor_custo') + somarProp(formData.seguros, 'valor_custo') + somarProp(formData.outros, 'valor_custo');
    const vendaProdutos = vendaAereo + somarProp(formData.hospedagens, 'valor_venda') + somarProp(formData.automoveis, 'valor_venda') + somarProp(formData.transfers, 'valor_venda') + somarProp(formData.passeios, 'valor_venda') + somarProp(formData.seguros, 'valor_venda') + somarProp(formData.outros, 'valor_venda');

    // Descontos entram na soma porque já são valores negativos no estado
    const custoExtras = somarProp(formData.bagagens, 'valor_custo') + somarProp(formData.reservas, 'valor_custo') + somarProp(formData.pets, 'valor_custo') + somarProp(formData.descontos, 'valor_custo');
    const vendaExtras = somarProp(formData.bagagens, 'valor_venda') + somarProp(formData.reservas, 'valor_venda') + somarProp(formData.pets, 'valor_venda') + somarProp(formData.descontos, 'valor_venda');

    const custoCalculado = custoProdutos + custoExtras;
    const valorTotalCalculado = vendaProdutos + vendaExtras;
    const lucroCalculado = valorTotalCalculado - custoCalculado;

    setFormData(prev => {
      let pg = prev.pagamentos, pgc = prev.pagamentos_custo;
      let alterado = false;
      const vtStr = valorTotalCalculado.toFixed(2), ctStr = custoCalculado.toFixed(2);

      if (pg.length === 1 && pg[0].valor !== vtStr) { pg = [{ ...pg[0], valor: vtStr }]; alterado = true; }
      if (pgc.length === 1 && pgc[0].valor !== ctStr) { pgc = [{ ...pgc[0], valor: ctStr }]; alterado = true; }
      if (prev.custo !== custoCalculado || prev.valor_total !== valorTotalCalculado) { alterado = true; }

      if (!alterado) return prev;
      return { ...prev, custo: custoCalculado, valor_total: valorTotalCalculado, lucro: lucroCalculado, pagamentos: pg, pagamentos_custo: pgc };
    });
  }, [formData.aereo_custo, formData.aereo_venda, formData.hospedagens, formData.automoveis, formData.transfers, formData.passeios, formData.seguros, formData.outros, formData.bagagens, formData.reservas, formData.pets, formData.descontos]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.clientes_ids.length === 0) return toast.error('Selecione um cliente');
    if (formData.categorias_selecionadas.length === 0) return toast.error('Selecione pelo menos uma categoria de serviço');

    setLoading(true);
    try {
      const mapItem = (t) => ({ ...t, localizador: t.localizador || "", fornecedor_nome: t.fornecedor_nome || "", valor_custo: parseFloat(t.valor_custo) || 0, valor_venda: parseFloat(t.valor_venda) || 0 });
      const mapExtra = (arr) => arr.map(i => ({ valor_custo: parseFloat(i.valor_custo) || 0, valor_venda: parseFloat(i.valor_venda) || 0 }));
      
      const taxa_bagagem_total = formData.bagagens.reduce((acc, b) => acc + (parseFloat(b.valor_custo) || 0), 0);
      const taxa_reserva_total = formData.reservas.reduce((acc, r) => acc + (parseFloat(r.valor_custo) || 0), 0);
      const taxa_pet_total = formData.pets.reduce((acc, p) => acc + (parseFloat(p.valor_custo) || 0), 0);
      const desconto_total_custo = formData.descontos.reduce((acc, d) => acc + (parseFloat(d.valor_custo) || 0), 0);

      const payload = {
        ...formData,
        categoria: formData.categorias_selecionadas.join(', '),
        valor_total: parseFloat(formData.valor_total),
        custo: parseFloat(formData.custo),
        
        taxa_bagagem_total,
        taxa_reserva_total,
        taxa_pet_total,
        desconto_total: -desconto_total_custo, 
        
        pagamentos: formData.pagamentos.filter(p => p.valor > 0).map(p => ({ ...p, valor: parseFloat(p.valor), num_parcelas: parseInt(p.num_parcelas) || 1 })),
        pagamentos_custo: formData.pagamentos_custo.filter(p => p.valor > 0).map(p => ({ ...p, valor: parseFloat(p.valor), num_parcelas: parseInt(p.num_parcelas) || 1 })),
        
        trechos: formData.trechos.map((t, index) => ({ 
            ...t, localizador: t.localizador || "", fornecedor_nome: t.fornecedor_nome || "",
            valor_custo: index === 0 ? (parseFloat(formData.aereo_custo) || 0) : 0, 
            valor_venda: index === 0 ? (parseFloat(formData.aereo_venda) || 0) : 0, 
            data_partida: t.data_partida || null, data_chegada: t.data_chegada || null
        })),
        hospedagens: formData.hospedagens.map(mapItem),
        automoveis: formData.automoveis.map(mapItem),
        transfers: formData.transfers.map(mapItem),
        passeios: formData.passeios.map(mapItem),
        seguros: formData.seguros.map(mapItem),
        outros: formData.outros.map(o => ({ descricao: o.descricao, valor_custo: parseFloat(o.valor_custo) || 0, valor_venda: parseFloat(o.valor_venda) || 0 })),
        
        bagagens: mapExtra(formData.bagagens),
        reservas: mapExtra(formData.reservas),
        pets: mapExtra(formData.pets),
        descontos: mapExtra(formData.descontos),
      };
      
      delete payload.aereo_custo; delete payload.aereo_venda; delete payload.valor_tarifa; delete payload.taxa_fornecedor; delete payload.taxas_adicionais;

      await axiosInstance.post('/vendas', payload);
      toast.success('Venda cadastrada com sucesso!');
      navigate('/vendas');
    } catch (error) {
      toast.error('Erro ao cadastrar venda');
    } finally {
      setLoading(false);
    }
  };

  const totalRecebido = formData.pagamentos.reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
  const diferencaRecebimento = formData.valor_total - totalRecebido;
  const totalCustoLancado = formData.pagamentos_custo.reduce((acc, p) => acc + (parseFloat(p.valor) || 0), 0);
  const diferencaCusto = formData.custo - totalCustoLancado;
  const margem = formData.valor_total > 0 ? ((formData.lucro / formData.valor_total) * 100).toFixed(2) : '0.00';

  return (
    <TooltipProvider>
        <div className="container mx-auto px-6 py-8">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2 pt-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Nova Venda</h1>
            <p className="text-gray-600">Monte pacotes combinando aéreo, hotel e serviços</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            
            <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Serviços / Produtos</h3>
                <div className="flex flex-wrap gap-4">
                    {CATEGORIAS_DISPONIVEIS.map(cat => (
                        <div key={cat} className={`flex items-center space-x-2 border p-3 rounded-lg transition-colors ${formData.categorias_selecionadas.includes(cat) ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'}`}>
                            <Checkbox id={`cat-${cat}`} checked={formData.categorias_selecionadas.includes(cat)} onCheckedChange={() => toggleCategoria(cat)} />
                            <Label htmlFor={`cat-${cat}`} className="cursor-pointer font-medium text-sm flex-1">{cat}</Label>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200">
                <ClienteMultiSelect clientes={clientes} selectedIds={formData.clientes_ids} onChange={(ids) => setFormData({ ...formData, clientes_ids: ids })} />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                        <Label>Localizador Master</Label>
                        <Input value={formData.localizador} onChange={(e) => setFormData({ ...formData, localizador: e.target.value })} required />
                    </div>
                    <div>
                        <Label>Vendedor</Label>
                        <Select value={formData.vendedor_id} onValueChange={(value) => setFormData({ ...formData, vendedor_id: value })}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>{vendedores.map(v => <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Fornecedor Master / Operadora</Label>
                        <Select value={formData.fornecedor_id} onValueChange={(value) => setFormData({ ...formData, fornecedor_id: value })}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>{fornecedores.map(f => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* --- BLOCOS DE SERVIÇOS --- */}
            
            {/* AÉREO COM ANCILARES INTEGRADOS */}
            {formData.categorias_selecionadas.includes('AEREO') && (
                <div className="bg-white rounded-xl p-6 border border-blue-200 border-l-4 border-l-blue-500 shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-blue-800">✈️ Passagens Aéreas</h3>
                        <Button type="button" size="sm" onClick={addTrechoInteligente}><Plus className="w-4 h-4 mr-2"/> Adicionar Trecho</Button>
                    </div>

                    {formData.trechos.map((item, index) => (
                        <div key={index} className="p-5 pt-10 bg-gray-50 border border-gray-200 rounded-lg mb-4 relative">
                            <span className="absolute top-3 left-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Trecho {index + 1}</span>
                            <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-red-500 hover:bg-red-100" onClick={() => removeItem('trechos', index)}><Trash2 className="w-4 h-4" /></Button>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label>Tipo</Label>
                                    <Select value={item.tipo} onValueChange={(val) => updateItem('trechos', index, 'tipo', val)}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="IDA">Ida</SelectItem>
                                            <SelectItem value="VOLTA">Volta</SelectItem>
                                            <SelectItem value="CONEXAO">Conexão</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div><Label>Cia Aérea</Label><AirlineAutocomplete value={item.companhia_id} onSelect={(val) => { updateItem('trechos', index, 'companhia_id', val.iata); updateItem('trechos', index, 'companhia_nome', val.name); }} /></div>
                                
                                <div><Label>Origem</Label><AirportAutocomplete value={item.origem} onSelect={(val) => updateItem('trechos', index, 'origem', val)} /></div>
                                <div><Label>Destino</Label><AirportAutocomplete value={item.destino} onSelect={(val) => updateItem('trechos', index, 'destino', val)} /></div>
                                
                                <div><Label>Data/Hora Partida</Label><Input type="datetime-local" value={item.data_partida} onChange={(e) => updateItem('trechos', index, 'data_partida', e.target.value)} required/></div>
                                <div><Label>Data/Hora Chegada</Label><Input type="datetime-local" value={item.data_chegada} onChange={(e) => updateItem('trechos', index, 'data_chegada', e.target.value)} /></div>

                                <div>
                                    <Label>Fornecedor (Emissor)</Label>
                                    <Select value={item.fornecedor_nome} onValueChange={(val) => updateItem('trechos', index, 'fornecedor_nome', val)}>
                                        <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                                        <SelectContent>{fornecedores.map(f => <SelectItem key={f.id} value={f.nome}>{f.nome}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div><Label>Localizador PNR</Label><Input value={item.localizador} onChange={(e) => updateItem('trechos', index, 'localizador', e.target.value)} placeholder="Opcional" /></div>
                            </div>
                        </div>
                    ))}

                    {/* Extras do Voo e Custo Global do Aéreo (Agrupados) */}
                    {formData.trechos.length > 0 && (
                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-bold text-blue-900">Ancilares (Serviços Adicionais do Voo)</h4>
                                <div className="space-x-2">
                                    <Button size="sm" type="button" variant="outline" onClick={() => addExtra('bagagens')} className="bg-white">+ Bagagem</Button>
                                    <Button size="sm" type="button" variant="outline" onClick={() => addExtra('reservas')} className="bg-white">+ Assento</Button>
                                    <Button size="sm" type="button" variant="outline" onClick={() => addExtra('pets')} className="bg-white">+ Pet</Button>
                                </div>
                            </div>
                            
                            <div className="space-y-2 mb-4">
                                {formData.bagagens.map(b => (
                                <div key={b.id} className="flex gap-2 items-center">
                                    <span className="text-xs font-medium w-16 text-blue-800">Bagagem:</span>
                                    <Input type="number" step="0.01" placeholder="Custo (R$)" value={b.valor_custo} onChange={(e) => updateExtra('bagagens', b.id, 'valor_custo', e.target.value)} className="h-8 bg-white" />
                                    <Input type="number" step="0.01" placeholder="Venda (R$)" value={b.valor_venda} onChange={(e) => updateExtra('bagagens', b.id, 'valor_venda', e.target.value)} className="h-8 bg-white" />
                                    <Button size="sm" type="button" variant="ghost" onClick={() => removeExtra('bagagens', b.id)} className="h-8 px-2"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                </div>
                                ))}
                                {formData.reservas.map(r => (
                                <div key={r.id} className="flex gap-2 items-center">
                                    <span className="text-xs font-medium w-16 text-blue-800">Assento:</span>
                                    <Input type="number" step="0.01" placeholder="Custo (R$)" value={r.valor_custo} onChange={(e) => updateExtra('reservas', r.id, 'valor_custo', e.target.value)} className="h-8 bg-white" />
                                    <Input type="number" step="0.01" placeholder="Venda (R$)" value={r.valor_venda} onChange={(e) => updateExtra('reservas', r.id, 'valor_venda', e.target.value)} className="h-8 bg-white" />
                                    <Button size="sm" type="button" variant="ghost" onClick={() => removeExtra('reservas', r.id)} className="h-8 px-2"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                </div>
                                ))}
                                {formData.pets.map(p => (
                                <div key={p.id} className="flex gap-2 items-center">
                                    <span className="text-xs font-medium w-16 text-blue-800">Pet:</span>
                                    <Input type="number" step="0.01" placeholder="Custo (R$)" value={p.valor_custo} onChange={(e) => updateExtra('pets', p.id, 'valor_custo', e.target.value)} className="h-8 bg-white" />
                                    <Input type="number" step="0.01" placeholder="Venda (R$)" value={p.valor_venda} onChange={(e) => updateExtra('pets', p.id, 'valor_venda', e.target.value)} className="h-8 bg-white" />
                                    <Button size="sm" type="button" variant="ghost" onClick={() => removeExtra('pets', p.id)} className="h-8 px-2"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                                </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 border-t border-blue-200 pt-4">
                                <div><Label className="text-blue-900 font-semibold">Custo Bilhete Aéreo (Fornecedor) R$</Label><Input type="number" step="0.01" value={formData.aereo_custo} onChange={(e) => setFormData({...formData, aereo_custo: e.target.value})} className="bg-white border-blue-300 focus-visible:ring-blue-500" required /></div>
                                <div><Label className="text-blue-900 font-semibold">Venda Bilhete Aéreo (Cliente) R$</Label><Input type="number" step="0.01" value={formData.aereo_venda} onChange={(e) => setFormData({...formData, aereo_venda: e.target.value})} className="bg-white border-blue-300 focus-visible:ring-blue-500" required /></div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* HOSPEDAGEM */}
            {formData.categorias_selecionadas.includes('HOSPEDAGEM') && (
                <div className="bg-white rounded-xl p-6 border border-yellow-200 border-l-4 border-l-yellow-500 shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-yellow-800">🏨 Hospedagem</h3>
                        <Button type="button" size="sm" onClick={() => addItem('hospedagens')}><Plus className="w-4 h-4 mr-2"/> Adicionar Hotel</Button>
                    </div>
                    {formData.hospedagens.map((item, index) => (
                        <div key={index} className="flex flex-col gap-4 p-5 pt-10 bg-gray-50 border border-gray-200 rounded-lg mb-4 relative">
                            <span className="absolute top-3 left-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Hotel {index + 1}</span>
                            <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-red-500 hover:bg-red-100" onClick={() => removeItem('hospedagens', index)}><Trash2 className="w-4 h-4" /></Button>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div><Label>Nome do Hotel</Label><Input value={item.hotel} onChange={(e) => updateItem('hospedagens', index, 'hotel', e.target.value)} /></div>
                                <div><Label>Quarto</Label><Input value={item.quarto} onChange={(e) => updateItem('hospedagens', index, 'quarto', e.target.value)} placeholder="Ex: Duplo Standard"/></div>
                                
                                <div><Label>Check-in</Label><Input type="date" value={item.checkin} onChange={(e) => updateItem('hospedagens', index, 'checkin', e.target.value)} /></div>
                                <div><Label>Check-out</Label><Input type="date" value={item.checkout} onChange={(e) => updateItem('hospedagens', index, 'checkout', e.target.value)} /></div>

                                <div>
                                    <Label>Fornecedor (Ex: Trend)</Label>
                                    <Select value={item.fornecedor_nome} onValueChange={(val) => updateItem('hospedagens', index, 'fornecedor_nome', val)}>
                                        <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                                        <SelectContent>{fornecedores.map(f => <SelectItem key={f.id} value={f.nome}>{f.nome}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div><Label>Reserva / Confirmação</Label><Input value={item.localizador} onChange={(e) => updateItem('hospedagens', index, 'localizador', e.target.value)} placeholder="Opcional" /></div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 mt-2 bg-yellow-100 rounded-lg border border-yellow-200">
                                <div><Label className="text-yellow-900 font-semibold">Custo (R$)</Label><Input type="number" step="0.01" value={item.valor_custo} onChange={(e) => updateItem('hospedagens', index, 'valor_custo', e.target.value)} className="bg-white border-yellow-300 focus-visible:ring-yellow-500" /></div>
                                <div><Label className="text-yellow-900 font-semibold">Venda Cliente (R$)</Label><Input type="number" step="0.01" value={item.valor_venda} onChange={(e) => updateItem('hospedagens', index, 'valor_venda', e.target.value)} className="bg-white border-yellow-300 focus-visible:ring-yellow-500" /></div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* AUTOMÓVEL */}
            {formData.categorias_selecionadas.includes('AUTOMOVEL') && (
                <div className="bg-white rounded-xl p-6 border border-slate-200 border-l-4 border-l-slate-500 shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-slate-800">🚗 Aluguel de Carro</h3>
                        <Button type="button" size="sm" onClick={() => addItem('automoveis')}><Plus className="w-4 h-4 mr-2"/> Adicionar Carro</Button>
                    </div>
                    {formData.automoveis.map((item, index) => (
                        <div key={index} className="flex flex-col gap-4 p-5 pt-10 bg-gray-50 border border-gray-200 rounded-lg mb-4 relative">
                            <span className="absolute top-3 left-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Veículo {index + 1}</span>
                            <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-red-500 hover:bg-red-100" onClick={() => removeItem('automoveis', index)}><Trash2 className="w-4 h-4" /></Button>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div><Label>Locadora</Label><Input value={item.locadora} onChange={(e) => updateItem('automoveis', index, 'locadora', e.target.value)} /></div>
                                <div><Label>Modelo/Categoria</Label><Input value={item.modelo} onChange={(e) => updateItem('automoveis', index, 'modelo', e.target.value)} /></div>
                                
                                <div><Label>Retirada (Data/Hora)</Label><Input type="datetime-local" value={item.retirada} onChange={(e) => updateItem('automoveis', index, 'retirada', e.target.value)} /></div>
                                <div><Label>Devolução (Data/Hora)</Label><Input type="datetime-local" value={item.devolucao} onChange={(e) => updateItem('automoveis', index, 'devolucao', e.target.value)} /></div>

                                <div>
                                    <Label>Broker (Ex: Rentcars)</Label>
                                    <Select value={item.fornecedor_nome} onValueChange={(val) => updateItem('automoveis', index, 'fornecedor_nome', val)}>
                                        <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                                        <SelectContent>{fornecedores.map(f => <SelectItem key={f.id} value={f.nome}>{f.nome}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div><Label>Voucher / Reserva</Label><Input value={item.localizador} onChange={(e) => updateItem('automoveis', index, 'localizador', e.target.value)} placeholder="Opcional" /></div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 mt-2 bg-slate-100 rounded-lg border border-slate-300">
                                <div><Label className="text-slate-900 font-semibold">Custo (R$)</Label><Input type="number" step="0.01" value={item.valor_custo} onChange={(e) => updateItem('automoveis', index, 'valor_custo', e.target.value)} className="bg-white border-slate-300 focus-visible:ring-slate-500" /></div>
                                <div><Label className="text-slate-900 font-semibold">Venda Cliente (R$)</Label><Input type="number" step="0.01" value={item.valor_venda} onChange={(e) => updateItem('automoveis', index, 'valor_venda', e.target.value)} className="bg-white border-slate-300 focus-visible:ring-slate-500" /></div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* TRANSFER */}
            {formData.categorias_selecionadas.includes('TRANSFER') && (
                <div className="bg-white rounded-xl p-6 border border-teal-200 border-l-4 border-l-teal-500 shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-teal-800">🚐 Transfer</h3>
                        <Button type="button" size="sm" onClick={() => addItem('transfers')}><Plus className="w-4 h-4 mr-2"/> Adicionar Transfer</Button>
                    </div>
                    {formData.transfers.map((item, index) => (
                        <div key={index} className="flex flex-col gap-4 p-5 pt-10 bg-gray-50 border border-gray-200 rounded-lg mb-4 relative">
                            <span className="absolute top-3 left-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Transfer {index + 1}</span>
                            <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-red-500 hover:bg-red-100" onClick={() => removeItem('transfers', index)}><Trash2 className="w-4 h-4" /></Button>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div><Label>Trajeto</Label><Input value={item.trajeto} onChange={(e) => updateItem('transfers', index, 'trajeto', e.target.value)} placeholder="Ex: Aeroporto -> Hotel" /></div>
                                <div><Label>Data/Hora</Label><Input type="datetime-local" value={item.data} onChange={(e) => updateItem('transfers', index, 'data', e.target.value)} /></div>
                                
                                <div>
                                    <Label>Fornecedor/Receptivo</Label>
                                    <Select value={item.fornecedor_nome} onValueChange={(val) => updateItem('transfers', index, 'fornecedor_nome', val)}>
                                        <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                                        <SelectContent>{fornecedores.map(f => <SelectItem key={f.id} value={f.nome}>{f.nome}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div><Label>Voucher / Ticket</Label><Input value={item.localizador} onChange={(e) => updateItem('transfers', index, 'localizador', e.target.value)} placeholder="Opcional" /></div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 mt-2 bg-teal-100 rounded-lg border border-teal-300">
                                <div><Label className="text-teal-900 font-semibold">Custo (R$)</Label><Input type="number" step="0.01" value={item.valor_custo} onChange={(e) => updateItem('transfers', index, 'valor_custo', e.target.value)} className="bg-white border-teal-300 focus-visible:ring-teal-500" /></div>
                                <div><Label className="text-teal-900 font-semibold">Venda Cliente (R$)</Label><Input type="number" step="0.01" value={item.valor_venda} onChange={(e) => updateItem('transfers', index, 'valor_venda', e.target.value)} className="bg-white border-teal-300 focus-visible:ring-teal-500" /></div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* PASSEIO */}
            {formData.categorias_selecionadas.includes('PASSEIO') && (
                <div className="bg-white rounded-xl p-6 border border-orange-200 border-l-4 border-l-orange-500 shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-orange-800">🗺️ Passeio / Ingresso</h3>
                        <Button type="button" size="sm" onClick={() => addItem('passeios')}><Plus className="w-4 h-4 mr-2"/> Adicionar Passeio</Button>
                    </div>
                    {formData.passeios.map((item, index) => (
                        <div key={index} className="flex flex-col gap-4 p-5 pt-10 bg-gray-50 border border-gray-200 rounded-lg mb-4 relative">
                            <span className="absolute top-3 left-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Atividade {index + 1}</span>
                            <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-red-500 hover:bg-red-100" onClick={() => removeItem('passeios', index)}><Trash2 className="w-4 h-4" /></Button>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div><Label>Descrição</Label><Input value={item.descricao} onChange={(e) => updateItem('passeios', index, 'descricao', e.target.value)} placeholder="Ex: City Tour" /></div>
                                <div><Label>Data/Hora</Label><Input type="datetime-local" value={item.data} onChange={(e) => updateItem('passeios', index, 'data', e.target.value)} /></div>
                                
                                <div>
                                    <Label>Fornecedor/Operadora</Label>
                                    <Select value={item.fornecedor_nome} onValueChange={(val) => updateItem('passeios', index, 'fornecedor_nome', val)}>
                                        <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                                        <SelectContent>{fornecedores.map(f => <SelectItem key={f.id} value={f.nome}>{f.nome}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div><Label>Ticket / Localizador</Label><Input value={item.localizador} onChange={(e) => updateItem('passeios', index, 'localizador', e.target.value)} placeholder="Opcional" /></div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 mt-2 bg-orange-100 rounded-lg border border-orange-300">
                                <div><Label className="text-orange-900 font-semibold">Custo (R$)</Label><Input type="number" step="0.01" value={item.valor_custo} onChange={(e) => updateItem('passeios', index, 'valor_custo', e.target.value)} className="bg-white border-orange-300 focus-visible:ring-orange-500" /></div>
                                <div><Label className="text-orange-900 font-semibold">Venda Cliente (R$)</Label><Input type="number" step="0.01" value={item.valor_venda} onChange={(e) => updateItem('passeios', index, 'valor_venda', e.target.value)} className="bg-white border-orange-300 focus-visible:ring-orange-500" /></div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* SEGURO VIAGEM */}
            {formData.categorias_selecionadas.includes('SEGURO') && (
                <div className="bg-white rounded-xl p-6 border border-emerald-200 border-l-4 border-l-emerald-500 shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-emerald-800">🛡️ Seguro Viagem</h3>
                        <Button type="button" size="sm" onClick={() => addItem('seguros')}><Plus className="w-4 h-4 mr-2"/> Adicionar Seguro</Button>
                    </div>
                    {formData.seguros.map((item, index) => (
                        <div key={index} className="flex flex-col gap-4 p-5 pt-10 bg-gray-50 border border-gray-200 rounded-lg mb-4 relative">
                            <span className="absolute top-3 left-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Seguro {index + 1}</span>
                            <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 text-red-500 hover:bg-red-100" onClick={() => removeItem('seguros', index)}><Trash2 className="w-4 h-4" /></Button>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div><Label>Seguradora</Label><Input value={item.seguradora} onChange={(e) => updateItem('seguros', index, 'seguradora', e.target.value)} placeholder="Ex: GTA, Universal Assistance" /></div>
                                <div><Label>Plano</Label><Input value={item.plano} onChange={(e) => updateItem('seguros', index, 'plano', e.target.value)} placeholder="Ex: Bronze 40 Europa" /></div>

                                <div><Label>Data de Início</Label><Input type="date" value={item.data_inicio} onChange={(e) => updateItem('seguros', index, 'data_inicio', e.target.value)} /></div>
                                <div><Label>Data de Fim</Label><Input type="date" value={item.data_fim} onChange={(e) => updateItem('seguros', index, 'data_fim', e.target.value)} /></div>

                                <div>
                                    <Label>Fornecedor (Emissor)</Label>
                                    <Select value={item.fornecedor_nome} onValueChange={(val) => updateItem('seguros', index, 'fornecedor_nome', val)}>
                                        <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                                        <SelectContent>{fornecedores.map(f => <SelectItem key={f.id} value={f.nome}>{f.nome}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div><Label>Apólice / Voucher</Label><Input value={item.localizador} onChange={(e) => updateItem('seguros', index, 'localizador', e.target.value)} placeholder="Opcional" /></div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 mt-2 bg-emerald-100 rounded-lg border border-emerald-300">
                                <div><Label className="text-emerald-900 font-semibold">Custo (R$)</Label><Input type="number" step="0.01" value={item.valor_custo} onChange={(e) => updateItem('seguros', index, 'valor_custo', e.target.value)} className="bg-white border-emerald-300 focus-visible:ring-emerald-500" /></div>
                                <div><Label className="text-emerald-900 font-semibold">Venda Cliente (R$)</Label><Input type="number" step="0.01" value={item.valor_venda} onChange={(e) => updateItem('seguros', index, 'valor_venda', e.target.value)} className="bg-white border-emerald-300 focus-visible:ring-emerald-500" /></div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* OUTROS SERVIÇOS E TAXAS */}
            {formData.categorias_selecionadas.includes('OUTROS') && (
                <div className="bg-white rounded-xl p-6 border border-gray-300 border-l-4 border-l-gray-600 shadow-sm">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-gray-800">📌 Outros Serviços / Taxas DU</h3>
                        <Button type="button" size="sm" onClick={() => addItem('outros')}><Plus className="w-4 h-4 mr-2"/> Adicionar Item</Button>
                    </div>
                    {formData.outros.map((item, index) => (
                        <div key={index} className="flex flex-col md:flex-row gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg mb-4 relative items-end">
                            <Button type="button" variant="ghost" size="sm" className="absolute top-2 right-2 md:relative md:top-0 md:right-0 text-red-500 hover:bg-red-100" onClick={() => removeItem('outros', index)}><Trash2 className="w-4 h-4" /></Button>
                            
                            <div className="flex-1 w-full"><Label>Descrição do Item</Label><Input value={item.descricao} onChange={(e) => updateItem('outros', index, 'descricao', e.target.value)} placeholder="Ex: Visto Americano, Chip, Fee" /></div>
                            <div className="w-full md:w-40"><Label className="text-gray-900 font-semibold">Custo (R$)</Label><Input type="number" step="0.01" value={item.valor_custo} onChange={(e) => updateItem('outros', index, 'valor_custo', e.target.value)} className="bg-white" /></div>
                            <div className="w-full md:w-40"><Label className="text-blue-600 font-semibold">Venda Cliente (R$)</Label><Input type="number" step="0.01" value={item.valor_venda} onChange={(e) => updateItem('outros', index, 'valor_venda', e.target.value)} className="bg-white" /></div>
                        </div>
                    ))}
                </div>
            )}

            {/* DESCONTOS GLOBAIS */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Descontos Globais</h3>
                    <Button size="sm" type="button" variant="outline" onClick={() => addExtra('descontos')}><Plus className="w-4 h-4 mr-2" /> Adicionar Desconto</Button>
                </div>
                {formData.descontos.length > 0 ? (
                    <div className="space-y-3">
                        {formData.descontos.map(d => (
                            <div key={d.id} className="flex gap-4 items-center p-3 bg-red-50 border border-red-100 rounded-lg">
                                <span className="text-sm font-medium w-24 text-red-800">Desconto:</span>
                                <div className="flex-1"><Label className="text-xs text-red-600">Redução de Custo (Opcional)</Label><Input type="number" step="0.01" value={d.valor_custo} onChange={(e) => updateExtra('descontos', d.id, 'valor_custo', e.target.value)} className="bg-white border-red-200" /></div>
                                <div className="flex-1"><Label className="text-xs text-red-600">Redução Cliente (Obrigatório)</Label><Input type="number" step="0.01" value={d.valor_venda} onChange={(e) => updateExtra('descontos', d.id, 'valor_venda', e.target.value)} className="bg-white border-red-200" /></div>
                                <Button size="sm" type="button" variant="ghost" onClick={() => removeExtra('descontos', d.id)} className="mt-4"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">Nenhum desconto aplicado nesta venda.</p>
                )}
            </div>
              
            <div className="py-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-1">
                    <h4 className="text-md font-semibold text-gray-800">1. Recebimentos (Cliente → Agência)</h4>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" className="inline-flex outline-none"><Info className="w-4 h-4 text-gray-400 cursor-help" /></button>
                      </TooltipTrigger>
                      <TooltipContent><p>O valor do primeiro recebimento é preenchido automaticamente com o "Total Venda".</p></TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-center space-x-2 bg-gray-50 px-3 py-1 rounded-md border border-gray-200">
                      <Switch id="switch-recebimento" checked={formData.liquidar_recebimentos} onCheckedChange={(c) => setFormData(prev => ({ ...prev, liquidar_recebimentos: c }))} />
                      <Label htmlFor="switch-recebimento" className={`text-xs cursor-pointer ${formData.liquidar_recebimentos ? 'text-green-600 font-bold' : 'text-gray-500'}`}>
                          {formData.liquidar_recebimentos ? 'Confirmar Recebimento (Pago)' : 'Lançar como Pendente'}
                      </Label>
                  </div>
                  <Button type="button" onClick={addPagamento} variant="outline" size="sm"><Plus className="w-4 h-4 mr-2" />Adicionar Recebimento</Button>
                </div>

                <div className="space-y-4">
                  {formData.pagamentos.map((pagto) => (
                    <div key={pagto.id} className="p-4 border border-gray-100 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div>
                          <Label>Forma de Pagamento</Label>
                          <Select value={pagto.forma_pagamento} onValueChange={(value) => updatePagamento(pagto.id, 'forma_pagamento', value)} required>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="PIX">PIX</SelectItem>
                              <SelectItem value="CARTAO_CREDITO">Cartão de Crédito</SelectItem>
                              <SelectItem value="BOLETO">Boleto</SelectItem>
                              <SelectItem value="CREDIARIO">Crediário Próprio</SelectItem>
                              <SelectItem value="DINHEIRO">Dinheiro</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Conta (Banco)</Label>
                          <Select value={pagto.conta_id} onValueChange={(value) => updatePagamento(pagto.id, 'conta_id', value)} required>
                            <SelectTrigger><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                            <SelectContent>{contas.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>Valor (R$)</Label><Input type="number" step="0.01" value={pagto.valor} onChange={(e) => updatePagamento(pagto.id, 'valor', e.target.value)} required /></div>
                        <div><Label>Nº de Parcelas</Label><Input type="number" value={pagto.num_parcelas} onChange={(e) => updatePagamento(pagto.id, 'num_parcelas', e.target.value)} min="1" required /></div>
                        <div><Label>{parseInt(pagto.num_parcelas, 10) > 1 ? "Venc. (1ª Parc.)" : "Vencimento"}</Label><Input type="date" value={pagto.data_vencimento} onChange={(e) => updatePagamento(pagto.id, 'data_vencimento', e.target.value)} required /></div>
                      </div>
                      {formData.pagamentos.length > 1 && (
                        <div className="flex justify-end mt-2"><Button type="button" onClick={() => removePagamento(pagto.id)} variant="ghost" size="sm" className="text-red-600"><Trash2 className="w-4 h-4 mr-1" /> Remover</Button></div>
                      )}
                    </div>
                  ))}
                </div>
                
                {formData.pagamentos.length > 0 && (
                  <div className={`mt-4 text-right font-semibold ${diferencaRecebimento === 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <p>Total da Venda: R$ {formData.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    <p>Total Lançado: R$ {totalRecebido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    <p>Diferença: R$ {diferencaRecebimento.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  </div>
                )}
              </div>

              <div className="pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-1">
                    <h4 className="text-md font-semibold text-gray-800">2. Pagamentos de Custo (Agência → Fornecedor)</h4>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" className="inline-flex outline-none"><Info className="w-4 h-4 text-gray-400 cursor-help" /></button>
                      </TooltipTrigger>
                      <TooltipContent><p>O valor do primeiro pagamento é preenchido automaticamente com o "Total Custo".</p></TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-center space-x-2 bg-gray-50 px-3 py-1 rounded-md border border-gray-200">
                      <Switch id="switch-custo" checked={formData.liquidar_custos} onCheckedChange={(c) => setFormData(prev => ({ ...prev, liquidar_custos: c }))} />
                      <Label htmlFor="switch-custo" className={`text-xs cursor-pointer ${formData.liquidar_custos ? 'text-green-600 font-bold' : 'text-gray-500'}`}>
                          {formData.liquidar_custos ? 'Confirmar Pagamento (Pago)' : 'Lançar como Pendente'}
                      </Label>
                  </div>
                  <Button type="button" onClick={addPagamentoCusto} variant="outline" size="sm"><Plus className="w-4 h-4 mr-2" />Adicionar Custo</Button>
                </div>

                <div className="space-y-4">
                  {formData.pagamentos_custo.map((pagto) => (
                    <div key={pagto.id} className="p-4 border border-gray-100 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div>
                          <Label>Pagamento (Custo)</Label>
                          <Select value={pagto.forma_pagamento} onValueChange={(value) => updatePagamentoCusto(pagto.id, 'forma_pagamento', value)} required>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="BOLETO">Boleto Fornecedor</SelectItem>
                              <SelectItem value="CARTAO_CREDITO">Cartão Crédito (Agência)</SelectItem>
                              <SelectItem value="PIX">PIX (Agência)</SelectItem>
                              <SelectItem value="OUTRO">Outro</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Conta (Banco)</Label>
                          <Select value={pagto.conta_id} onValueChange={(value) => updatePagamentoCusto(pagto.id, 'conta_id', value)} required>
                            <SelectTrigger><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                            <SelectContent>{contas.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>Valor (R$)</Label><Input type="number" step="0.01" value={pagto.valor} onChange={(e) => updatePagamentoCusto(pagto.id, 'valor', e.target.value)} required /></div>
                        <div><Label>Nº de Parcelas</Label><Input type="number" value={pagto.num_parcelas} onChange={(e) => updatePagamentoCusto(pagto.id, 'num_parcelas', e.target.value)} min="1" required /></div>
                        <div><Label>{parseInt(pagto.num_parcelas, 10) > 1 ? "Venc. (1ª Parc.)" : "Vencimento"}</Label><Input type="date" value={pagto.data_vencimento} onChange={(e) => updatePagamentoCusto(pagto.id, 'data_vencimento', e.target.value)} required /></div>
                      </div>
                      {formData.pagamentos_custo.length > 1 && (
                        <div className="flex justify-end mt-2"><Button type="button" onClick={() => removePagamentoCusto(pagto.id)} variant="ghost" size="sm" className="text-red-600"><Trash2 className="w-4 h-4 mr-1" /> Remover</Button></div>
                      )}
                    </div>
                  ))}
                </div>

                {(formData.pagamentos_custo.length > 0 && formData.custo > 0) && (
                  <div className={`mt-4 text-right font-semibold ${diferencaCusto === 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <p>Custo Total: R$ {formData.custo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    <p>Total Lançado: R$ {totalCustoLancado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    <p>Diferença: R$ {diferencaCusto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                  </div>
                )}
              </div>
            
            <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Resultado da Venda (Automático)</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div><p className="text-sm text-gray-600">Total Venda (Cliente)</p><p className="text-xl text-blue-600">R$ {formData.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p></div>
                    <div><p className="text-sm text-gray-600">Total Custo (Fornecedor)</p><p className="text-xl text-orange-600">R$ {formData.custo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p></div>
                    <div><p className="text-sm text-gray-600">Lucro Estimado</p><p className={`text-xl ${formData.lucro >= 0 ? 'text-green-600' : 'text-red-600'}`}>R$ {formData.lucro.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p><p className="text-sm text-gray-500">Margem: {margem}%</p></div>
                </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Observações</h3>
                <Textarea value={formData.observacao} onChange={(e) => setFormData({ ...formData, observacao: e.target.value })} rows={4} placeholder="Observações adicionais..." />
            </div>

            <div className="flex items-center space-x-4">
              <Button type="submit" disabled={loading} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                {loading ? 'Salvando...' : 'Finalizar Venda'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/vendas')}>Cancelar</Button>
            </div>
          </form>
        </div>
    </TooltipProvider>
  );
}